import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const PaymentForm = ({ selectedTier, onPaymentComplete, onBack }) => {
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    billingZip: ''
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    let formattedValue = value;

    if (name === 'cardNumber') {
      formattedValue = value?.replace(/\s/g, '')?.replace(/(\d{4})/g, '$1 ')?.trim();
      if (formattedValue?.length > 19) return;
    }

    if (name === 'expiryDate') {
      formattedValue = value?.replace(/\D/g, '');
      if (formattedValue?.length >= 2) {
        formattedValue = formattedValue?.slice(0, 2) + '/' + formattedValue?.slice(2, 4);
      }
      if (formattedValue?.length > 5) return;
    }

    if (name === 'cvv') {
      formattedValue = value?.replace(/\D/g, '')?.slice(0, 4);
    }

    if (name === 'billingZip') {
      formattedValue = value?.replace(/\D/g, '')?.slice(0, 5);
    }

    setPaymentData(prev => ({ ...prev, [name]: formattedValue }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!paymentData?.cardNumber || paymentData?.cardNumber?.replace(/\s/g, '')?.length < 16) {
      newErrors.cardNumber = 'Please enter a valid 16-digit card number';
    }

    if (!paymentData?.cardName || paymentData?.cardName?.trim()?.length < 3) {
      newErrors.cardName = 'Please enter the name on card';
    }

    if (!paymentData?.expiryDate || paymentData?.expiryDate?.length < 5) {
      newErrors.expiryDate = 'Please enter expiry date (MM/YY)';
    }

    if (!paymentData?.cvv || paymentData?.cvv?.length < 3) {
      newErrors.cvv = 'Please enter CVV';
    }

    if (!paymentData?.billingZip || paymentData?.billingZip?.length < 5) {
      newErrors.billingZip = 'Please enter 5-digit ZIP code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsProcessing(true);

    setTimeout(() => {
      setIsProcessing(false);
      onPaymentComplete();
    }, 2000);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Payment Information
        </h3>
        <div className="flex items-center space-x-2">
          <Icon name="Lock" size={16} className="text-success" />
          <span className="text-xs text-muted-foreground">Secure Payment</span>
        </div>
      </div>
      <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">{selectedTier?.name} Plan</p>
            <p className="text-xs text-muted-foreground">{selectedTier?.quota}</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-heading font-bold text-foreground">
              ${selectedTier?.price}
            </p>
            <p className="text-xs text-muted-foreground">per month</p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Card Number"
          type="text"
          name="cardNumber"
          placeholder="1234 5678 9012 3456"
          value={paymentData?.cardNumber}
          onChange={handleInputChange}
          error={errors?.cardNumber}
          required
        />

        <Input
          label="Cardholder Name"
          type="text"
          name="cardName"
          placeholder="John Doe"
          value={paymentData?.cardName}
          onChange={handleInputChange}
          error={errors?.cardName}
          required
        />

        <div className="grid grid-cols-3 gap-4">
          <Input
            label="Expiry Date"
            type="text"
            name="expiryDate"
            placeholder="MM/YY"
            value={paymentData?.expiryDate}
            onChange={handleInputChange}
            error={errors?.expiryDate}
            required
          />

          <Input
            label="CVV"
            type="text"
            name="cvv"
            placeholder="123"
            value={paymentData?.cvv}
            onChange={handleInputChange}
            error={errors?.cvv}
            required
          />

          <Input
            label="ZIP Code"
            type="text"
            name="billingZip"
            placeholder="12345"
            value={paymentData?.billingZip}
            onChange={handleInputChange}
            error={errors?.billingZip}
            required
          />
        </div>

        <div className="flex items-start space-x-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5 text-primary" />
          <p className="text-xs text-foreground">
            Your subscription includes automatic access to our exclusive Skool community where you can connect with other authors and get publishing guidance.
          </p>
        </div>

        <div className="flex items-center space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isProcessing}
            className="flex-1"
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="default"
            loading={isProcessing}
            iconName="CreditCard"
            iconPosition="left"
            className="flex-1"
          >
            {isProcessing ? 'Processing...' : `Pay $${selectedTier?.price}`}
          </Button>
        </div>
      </form>
      <div className="flex items-center justify-center space-x-4 mt-6 pt-6 border-t border-border">
        <Icon name="Shield" size={20} className="text-muted-foreground" />
        <Icon name="Lock" size={20} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Powered by Stripe</span>
      </div>
    </div>
  );
};

export default PaymentForm;